// src/components/Departamentos/DepartamentoModal.tsx
import { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { Departamento, Colaborador } from '../../types';

interface DepartamentoModalProps {
  departamento?: Departamento | null;
  colaboradores: Colaborador[];
  onSave: (departamento: Departamento | Omit<Departamento, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onClose: () => void;
}

const DepartamentoModal: React.FC<DepartamentoModalProps> = ({ 
  departamento, 
  colaboradores, 
  onSave, 
  onClose 
}) => {
  const [form, setForm] = useState({
    nome: '',
    descricao: '',
    responsavel: ''
  });

  // Filtra colaboradores do departamento atual
  const colaboradoresDepartamento = departamento
    ? colaboradores.filter(col => col.departamentoId === departamento.id)
    : [];

  useEffect(() => {
    if (departamento) {
      setForm({
        nome: departamento.nome,
        descricao: departamento.descricao,
        responsavel: departamento.responsavel
      });
    }
  }, [departamento]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nome || !form.descricao) {
      alert('Por favor, preencha nome e descrição do departamento');
      return;
    }

    if (departamento) {
      onSave({
        ...departamento,
        ...form
      });
    } else {
      // Ao criar, o componente pai definirá o empresaId correto
      onSave({
        ...form
      } as any);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-gradient-to-br from-blue-50 via-white to-purple-50 rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {departamento ? 'Editar Departamento' : 'Novo Departamento'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome do Departamento *
            </label>
            <input
              type="text"
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Ex: Recursos Humanos"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição *
            </label>
            <textarea
              value={form.descricao}
              onChange={(e) => setForm({ ...form, descricao: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              rows={3}
              placeholder="Descreva o papel e responsabilidades do departamento"
              required
            />
          </div>

          {departamento && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Responsável
              </label>
              {colaboradoresDepartamento.length > 0 ? (
                <select
                  value={form.responsavel}
                  onChange={(e) => setForm({ ...form, responsavel: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Selecione um responsável</option>
                  {colaboradoresDepartamento.map((col) => (
                    <option key={col.id} value={col.id}>
                      {col.nome} - {col.cargo}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                  Para definir um responsável, primeiro adicione colaboradores a este departamento na seção de Colaboradores.
                </div>
              )}
            </div>
          )}

          <div className="pt-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-lg text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            >
              {departamento ? 'Salvar Alterações' : 'Criar Departamento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DepartamentoModal;